import { useMemo } from 'react';
import { Typography, Tag } from 'antd';
import { SmartDataTableProps } from '@src/components/SmartDataTable/types';
import {
  useUserDateRender,
  useUserTimestampRender,
  useUserAvatarRender,
  useScheduledDateRender,
} from '@src/hooks';
import { TherapistType, THERAPIST_TYPE_COLORS } from '../constants';

const { Text } = Typography;

export const useTreatmentsColumns = (): SmartDataTableProps['columns'] => {
  const renderUserAvatar = useUserAvatarRender();
  const renderFollowUpDate = useScheduledDateRender();
  const userTimestampRender = useUserTimestampRender({
    size: 'small',
  });

  return useMemo(
    () => [
      {
        key: 'id',
        title: 'ID',
        dataIndex: 'id',
        minWidth: 80,
        render: (value: any) => (
          <Text
            style={{
              fontSize: '12px',
              color: '#8c8c8c',
              backgroundColor: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '4px',
            }}
          >
            #{value}
          </Text>
        ),
      },
      {
        key: 'treatment_name',
        title: 'Treatment',
        dataIndex: 'treatment_name',
        minWidth: 180,
        render: (value: string | string[]) => {
          const names = Array.isArray(value)
            ? value.filter(Boolean).map(String)
            : String(value || '')
                .split(',')
                .map(s => s.trim())
                .filter(Boolean);
          if (!names.length) {
            return <Text type="secondary">N/A</Text>;
          }
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                lineHeight: 1.45,
                maxWidth: '280px',
              }}
            >
              {names.map((name, index) => (
                <Text
                  key={`${name}-${index}`}
                  style={{
                    display: 'block',
                    fontWeight: 500,
                    fontSize: '13px',
                    color: '#262626',
                    wordBreak: 'break-word',
                  }}
                >
                  {name}
                </Text>
              ))}
            </div>
          );
        },
      },
      {
        key: 'body_part',
        title: 'Body Part',
        dataIndex: 'body_part',
        minWidth: 120,
        render: (value: string) => {
          if (!value) return <Text type="secondary">N/A</Text>;

          // Split comma-separated string into array and render as multiple tags
          const bodyParts = value.split(',').filter(Boolean);

          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {bodyParts.map((part, index) => (
                <Tag
                  key={index}
                  color="blue"
                  style={{ margin: 0, fontSize: '11px' }}
                >
                  {part.trim()}
                </Tag>
              ))}
            </div>
          );
        },
      },
      {
        key: 'follow_up_date',
        title: 'Follow Up Date',
        dataIndex: 'follow_up_date',
        sorter: true,
        minWidth: 180,
        render: (followUpDate: any) =>
          renderFollowUpDate(followUpDate, 'dateTime'),
      },
      {
        key: 'doctor',
        title: 'Doctor',
        dataIndex: 'under_observation_doctor_user_id',
        minWidth: 100,
        render: (_: any, record: any) =>
          renderUserAvatar({
            id: record.under_observation_doctor_user_id,
            first_name: record.doctor?.first_name,
            last_name: record.doctor?.last_name,
            type: 'doctor',
          }),
      },
      {
        key: 'therapist',
        title: 'Therapist',
        dataIndex: 'therapist_id',
        minWidth: 100,
        render: (_: any, record: any) =>
          renderUserAvatar({
            id: record.therapist_id,
            first_name: record.therapist?.first_name,
            last_name: record.therapist?.last_name,
            type: 'therapist',
          }),
      },
      {
        key: 'therapist_type',
        title: 'Type',
        dataIndex: 'therapist_type',
        minWidth: 100,
        render: (value: TherapistType) => (
          <Tag
            color={THERAPIST_TYPE_COLORS[value] || 'default'}
            style={{ margin: 0 }}
          >
            {value || '-'}
          </Tag>
        ),
      },
      {
        key: 'session_number',
        title: 'Session',
        dataIndex: 'session_number',
        minWidth: 100,
        render: (value: number) => (
          <Tag color="orange" style={{ margin: 0 }}>
            {value || 'N/A'}
          </Tag>
        ),
      },
      {
        key: 'machine',
        title: 'Machine',
        dataIndex: 'machine_name',
        minWidth: 200,
        render: (name: string, record: any) => (
          <div>
            <div style={{ fontWeight: 500 }}>{name || '-'}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.machine_starting_reading &&
                record.machine_ending_reading && (
                  <>
                    {record.machine_starting_reading} →{' '}
                    {record.machine_ending_reading}
                  </>
                )}
            </Text>
          </div>
        ),
      },
      {
        key: 'power_duration',
        title: 'Power/Duration',
        dataIndex: 'power_duration',
        minWidth: 200,
        render: (value: string) => {
          if (!value) return <Text type="secondary">-</Text>;

          // Split comma-separated string into array and render as multiple tags
          const powerDurations = value.split(',').filter(Boolean);

          return (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px',
              }}
            >
              {powerDurations.map((duration, index) => (
                <Tag
                  key={index}
                  color="green"
                  style={{ margin: 0, fontSize: '11px' }}
                >
                  {duration.trim()}
                </Tag>
              ))}
            </div>
          );
        },
      },
      {
        key: 'wavelength',
        title: 'Wavelength',
        dataIndex: 'wavelength',
        minWidth: 120,
        render: (value: string) => {
          if (!value || value === '0' || value === null) {
            return <Text type="secondary">-</Text>;
          }

          return (
            <Tag color="purple" style={{ margin: 0 }}>
              {value}
            </Tag>
          );
        },
      },

      {
        key: 'user_timestamp',
        title: 'User & Timestamp',
        dataIndex: 'user_info',
        minWidth: 200,
        render: (value: any, record: any) => userTimestampRender(record),
      },
    ],
    [renderUserAvatar, userTimestampRender]
  );
};
